export const qagentDocument = {
  contextPanel: {
    projectResources: 'Project Resources',
    files: {
      title: 'Files',
      uploadTooltip: 'Upload files to project',
      empty: 'No files yet',
      uploadButton: 'Upload files',
      viewTooltip: 'View file',
      deleteTooltip: 'Delete file',
      processing: 'processing...',
    },
    playbooks: {
      title: 'Playbooks',
      linkTooltip: 'Link existing playbook',
      createTooltip: 'Create new playbook',
      uploadTooltip: 'Upload playbook file',
      empty: 'No playbooks linked',
      linkButton: 'Link a playbook',
      viewTooltip: 'View playbook',
      unlinkTooltip: 'Unlink playbook',
    },
    activeContext: {
      files: (count: number) => `${count} file${count !== 1 ? 's' : ''}`,
    },
  },
  suggestions: {
    title: 'Suggestions',
    pendingBadge: (count: number) => `${count} pending`,
    types: {
      edit: 'Edit',
      insert: 'Insert',
      delete: 'Delete',
    } as Record<string, string>,
    accepted: 'Accepted',
    rejected: 'Rejected',
    applied: 'Applied',
    accept: 'Accept',
    reject: 'Reject',
    undo: 'Undo',
    view: 'View',
    acceptedCount: (count: number) => `${count} accepted`,
    rejectedCount: (count: number) => `${count} rejected`,
    general: 'General',
    downloadWithEditsTooltip: 'Download document with suggested edits applied',
    download: 'Download',
    preparing: 'Preparing...',
    paragraph: (n: number) => `Paragraph ${n}`,
    more: (count: number) => `+${count} more`,
    acceptAll: 'Accept All',
    rejectAll: 'Reject All',
  },
  documentViewer: {
    // Follows the bold count: "<b>3</b> pending suggestions"
    pendingSuggestions: (count: number) =>
      ` pending suggestion${count !== 1 ? 's' : ''}`,
    allReviewed: 'All suggestions reviewed',
    zoomOut: 'Zoom out',
    zoomIn: 'Zoom in',
    fitToWidth: 'Fit to width',
    changes: 'Changes',
    hide: 'Hide',
    show: 'Show',
    loading: 'Loading document...',
    loadFailed: 'Failed to load document',
    acceptedChanges: 'Accepted Changes',
    notExported: (count: number) =>
      `${count} change${count !== 1 ? 's' : ''} not yet exported`,
    noAcceptedChanges: 'No accepted changes yet',
    markup: 'Markup',
    markupTooltip: 'Show accepted changes as tracked changes (turn off for a clean view)',
  },
  exportMenu: {
    redline: 'Word redline',
    redlineHint: 'Tracked changes, no comments. Ready to send.',
    redlineWithComments: 'Redline with comments',
    redlineWithCommentsHint: "Tracked changes plus Queri's reasoning as Word comments. For internal use.",
    clean: 'Clean copy',
    cleanHint: 'Changes applied without tracking. Saves a new version of the file.',
    redlineNeedsWord: 'A redline needs a Word (.docx) file.',
  },
  fileViewer: {
    documentFallback: 'Document',
    download: 'Download',
    downloadWithChanges: 'Download with changes',
    close: 'Close file viewer',
    loading: 'Loading document...',
    loadFailed: 'Failed to load file',
    downloadFailed: 'Failed to download file',
    changesSkipped: (skipped: number) =>
      `${skipped} accepted changes could not be applied because their original text was not found in the document. They were left out, so review the document.`,
    retry: 'Retry',
    unsupported: 'Unsupported file type',
    downloadInstead: 'Download instead',
  },
  playbookViewer: {
    fallbackName: 'Playbook',
    close: 'Close viewer',
    loading: 'Loading playbook...',
    loadFailed: 'Failed to load playbook',
    active: 'Active',
    inactive: 'Inactive',
    ruleCount: (count: number) => `${count} rule${count !== 1 ? 's' : ''}`,
    activeCount: (count: number) => ` (${count} active)`,
    noRules: 'No rules defined',
  },
  chatMessage: {
    queued: 'Queued',
    yesterday: (time: string) => `Yesterday, ${time}`,
    dateTime: (dateStr: string, time: string) => `${dateStr}, ${time}`,
    thinking: 'Thinking',
    defaultWorking: 'Working',
    cancelled: 'Cancelled',
    running: 'Running',
    copy: 'Copy',
    exportWord: 'Download as Word',
    like: 'Like',
    dislike: 'Dislike',
    regenerate: 'Regenerate',
    queriing: 'Queri-ing',
    toolLabels: {
      thinking: 'Thinking',
      web_search: 'Searching the web',
      search_rag_database: 'Searching documents',
      knowledge_search: 'Searching documents',
      suggest_edit: 'Suggesting edit',
      suggest_insert: 'Suggesting insertion',
      suggest_delete: 'Suggesting deletion',
      read_section: 'Reading document section',
      get_diff: 'Reviewing changes',
      plan_review: 'Planning the review',
      check_redline: 'Checking the redline',
      withdraw_suggestion: 'Withdrawing a suggestion',
      remember: 'Saving to memory',
      generate_contract_draft: 'Drafting contract',
      export_document: 'Exporting document',
      plan_parallel_review: 'Planning parallel review',
      section_review: 'Reviewing section',
      merge_results: 'Combining review results',
      generating: 'Generating document',
      processingDetails: 'Processing details',
      processingInformation: 'Processing information',
      preparingSections: 'Preparing sections',
      orderingSections: 'Ordering sections',
      generatingSections: 'Generating sections',
      checkingDuplicates: 'Checking for duplicates',
      removingDuplicates: 'Removing duplicates',
      polishingContent: 'Polishing content',
      finalQualityCheck: 'Final quality check',
      fetchingPlaybookRules: 'Fetching playbook rules',
      generatingBlueprint: 'Planning the contract',
      blueprintGenerated: 'Contract plan ready',
      assemblingResults: 'Assembling sections',
      regeneratingSection: 'Regenerating section',
      validatingContract: 'Validating contract',
      savingContract: 'Saving contract',
    },
    skillLabels: {
      review: 'Reviewing',
    },
  },
  citations: {
    sources: (count: number) => `${count} source${count !== 1 ? 's' : ''}`,
    retrieved: (date: string) => `Retrieved ${date}`,
  },
  documentNotices: {
    truncated: (fileName: string, shown: number, total: number) =>
      `Only the first ${Math.round(shown / 1000)}k of ${Math.round(total / 1000)}k characters of "${fileName}" were read. The answer may miss clauses further into the document.`,
    summarised: (fileName: string) =>
      `"${fileName}" is long, so the assistant first saw only its outline and reads sections as needed. Check clause references against the document.`,
  },
};

export type QagentDocumentDict = typeof qagentDocument;
